import { useEffect, useMemo, useState } from "react";
import {
    ConfigProvider, DatePicker, Select, Card, Table, Tag, Space, Button, Modal, Form,
    Input, InputNumber, Row, Col, Statistic, Alert, Tooltip, Typography, Empty,
} from "antd";
import frFR from "antd/locale/fr_FR";
import {
    PlusOutlined, ImportOutlined, ReloadOutlined, EditOutlined, DeleteOutlined,
    PercentageOutlined, ExclamationCircleFilled, PieChartOutlined, BankOutlined,
    TeamOutlined, CheckCircleOutlined, SearchOutlined, InfoCircleOutlined,
    ExperimentOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import SideBar from "../../../../components/SideBar";
import NavBar from "../../../../components/NavBar";
import Footer from "../../../../components/Footer";
import axiosInstance from "../../../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../../../variables/constants";
import ClesRepartitionImport from "./ClesRepartitionImport";

dayjs.extend(isBetween);
const { confirm } = Modal;
const { RangePicker } = DatePicker;
const { Text, Title } = Typography;
const PRIMARY = "#242166";

// Filtres persistés (convention v2) — init paresseuse depuis sessionStorage.
const readSS = (k, def) => {
    try { const v = sessionStorage.getItem(k); return v !== null ? JSON.parse(v) : def; } catch { return def; }
};

const ClesRepartitionV2 = () => {
    const [cles, setCles] = useState([]);
    const [branches, setBranches] = useState([]);
    const [compagnies, setCompagnies] = useState([]);
    const [exercices, setExercices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingCle, setEditingCle] = useState(null);
    const [importModalVisible, setImportModalVisible] = useState(false);
    const [form] = Form.useForm();
    const navigate = useNavigate();

    // Filtres (persistés) + recherche texte.
    const [filtreBranche, setFiltreBranche] = useState(() => readSS("cleRepV2_branche", null));
    const [filtreCompagnie, setFiltreCompagnie] = useState(() => readSS("cleRepV2_compagnie", null));
    const [filtreExercice, setFiltreExercice] = useState(() => readSS("cleRepV2_exercice", null));
    const [recherche, setRecherche] = useState("");

    const isPoolUser = LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL";

    useEffect(() => { chargerDonneesInitiales(); }, []);
    useEffect(() => { chargerCles(); }, [filtreBranche, filtreCompagnie, filtreExercice]);
    useEffect(() => { sessionStorage.setItem("cleRepV2_branche", JSON.stringify(filtreBranche ?? null)); }, [filtreBranche]);
    useEffect(() => { sessionStorage.setItem("cleRepV2_compagnie", JSON.stringify(filtreCompagnie ?? null)); }, [filtreCompagnie]);
    useEffect(() => { sessionStorage.setItem("cleRepV2_exercice", JSON.stringify(filtreExercice ?? null)); }, [filtreExercice]);

    const chargerDonneesInitiales = async () => {
        try {
            const [brancheRes, compRes, exRes] = await Promise.all([
                axiosInstance.get("/api/branche"),
                axiosInstance.get("/api/organisme/assurance"),
                axiosInstance.get("/api/exercice"),
            ]);
            setBranches(brancheRes.data || []);
            setCompagnies(compRes.data || []);
            setExercices(exRes.data || []);
        } catch (err) {
            console.error(err);
            toast.error("Erreur lors du chargement des données");
        }
    };

    const chargerCles = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filtreBranche) params.branche = filtreBranche;
            if (filtreCompagnie) params.compagnie = filtreCompagnie;
            if (filtreExercice) params.exercice = filtreExercice;
            const response = await axiosInstance.get("/api/cle-repartition", { params });
            setCles(response.data || []);
        } catch (err) {
            console.error(err);
            toast.error("Erreur lors du chargement des clés de répartition");
        } finally {
            setLoading(false);
        }
    };

    const ouvrirModalCreation = () => { setEditingCle(null); form.resetFields(); setModalVisible(true); };
    const ouvrirModalEdition = (cle) => {
        setEditingCle(cle);
        form.setFieldsValue({
            compagnie: cle?.compagnie?.code,
            branche: cle?.branche?.codeBranche,
            exercice: cle?.exercice?.exerciceId,
            cle: cle.cle,
            periode: [dayjs(cle?.dateDebut), dayjs(cle?.dateFin)],
            libelle: cle?.commentaire,
        });
        setModalVisible(true);
    };

    const supprimerCle = (cle) => {
        confirm({
            title: "Confirmer la suppression",
            icon: <ExclamationCircleFilled />,
            content: `Voulez-vous vraiment supprimer la clé de répartition pour ${cle.compagnie.raisonSociale} ?`,
            okText: "Supprimer", okType: "danger", cancelText: "Annuler",
            onOk: async () => {
                try {
                    await axiosInstance.delete(`/api/cle-repartition/${cle.cleRepartitionId}`);
                    toast.success("Clé de répartition supprimée");
                    chargerCles();
                } catch (err) { console.error(err); toast.error("Erreur lors de la suppression"); }
            },
        });
    };

    const verifierChevauchement = async (values) => {
        try {
            const response = await axiosInstance.post("/api/cle-repartition/verifier-chevauchement", {
                compagnieCode: values.compagnie,
                brancheCode: values.branche,
                dateDebut: values.periode[0].format("YYYY-MM-DD"),
                dateFin: values.periode[1].format("YYYY-MM-DD"),
                cleRepartitionId: editingCle?.cleRepartitionId,
            });
            if (response.data.chevauchement) {
                toast.warning("Une clé de répartition existe déjà pour cette période");
                return false;
            }
            return true;
        } catch (err) { console.error(err); return true; }
    };

    const validerFormulaire = async (values) => {
        const ok = await verifierChevauchement(values);
        if (!ok) return;
        try {
            const cleData = {
                compagnie: values.compagnie,
                branche: values.branche,
                exercice: values.exercice,
                cle: values.cle,
                dateDebut: values.periode[0].format("YYYY-MM-DD"),
                dateFin: values.periode[1].format("YYYY-MM-DD"),
                commentaire: values.libelle,
                utilisateur: LOGGED_USER,
                cleRepartitionId: editingCle?.cleRepartitionId,
            };
            if (editingCle) {
                await axiosInstance.put("/api/cle-repartition", cleData);
                toast.success("Clé de répartition modifiée");
            } else {
                await axiosInstance.post("/api/cle-repartition", cleData);
                toast.success("Clé de répartition créée");
            }
            setModalVisible(false);
            form.resetFields();
            chargerCles();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || "Erreur lors de l'enregistrement");
        }
    };

    const reinitialiserFiltres = () => {
        setFiltreBranche(null); setFiltreCompagnie(null); setFiltreExercice(null); setRecherche("");
    };

    // Recherche texte (compagnie / branche / exercice) par-dessus les filtres serveur.
    const clesFiltrees = useMemo(() => {
        const q = recherche.trim().toLowerCase();
        if (!q) return cles;
        return cles.filter((c) => [
            c?.compagnie?.raisonSociale, c?.compagnie?.code, c?.branche?.nom, c?.branche?.codeBranche,
            c?.exercice?.libelleExercice, c?.commentaire,
        ].some((v) => (v || "").toString().toLowerCase().includes(q)));
    }, [cles, recherche]);

    const stats = useMemo(() => {
        const totalCles = clesFiltrees.length;
        const compagniesUniques = new Set(clesFiltrees.map((c) => c.compagnie?.code)).size;
        const tauxMoyen = totalCles > 0 ? clesFiltrees.reduce((s, c) => s + (c.cle || 0), 0) / totalCles : 0;
        const now = dayjs();
        const clesActives = clesFiltrees.filter((c) => now.isBetween(dayjs(c.dateDebut), dayjs(c.dateFin), null, "[]")).length;
        return { totalCles, compagniesUniques, tauxMoyen, clesActives };
    }, [clesFiltrees]);

    const statutTag = (record) => {
        const now = dayjs(), d = dayjs(record.dateDebut), f = dayjs(record.dateFin);
        if (now.isBefore(d)) return <Tag color="default">À venir</Tag>;
        if (now.isAfter(f)) return <Tag color="error">Expirée</Tag>;
        return <Tag color="success">Active</Tag>;
    };

    const colonnes = [
        {
            title: "Exercice", key: "exercice", width: 110,
            render: (_, r) => <span style={{ fontWeight: 600 }}>{r?.exercice?.libelleExercice || "—"}</span>,
            sorter: (a, b) => (a?.exercice?.libelleExercice || "").localeCompare(b?.exercice?.libelleExercice || ""),
        },
        {
            title: "Branche", key: "branche", width: 120,
            render: (_, r) => <Tag color="blue">{r?.branche?.nom || r?.branche?.codeBranche || "—"}</Tag>,
            sorter: (a, b) => (a?.branche?.codeBranche || "").localeCompare(b?.branche?.codeBranche || ""),
        },
        {
            title: "Compagnie", dataIndex: "compagnie", key: "compagnie",
            render: (compagnie) => (
                <div>
                    <div style={{ fontWeight: 600 }}>{compagnie?.raisonSociale}</div>
                    <div style={{ fontSize: 12, color: "#888" }}>{compagnie?.code}</div>
                </div>
            ),
            sorter: (a, b) => (a.compagnie?.raisonSociale || "").localeCompare(b.compagnie?.raisonSociale || ""),
        },
        {
            title: "Taux", dataIndex: "cle", key: "cle", align: "center", width: 120,
            render: (cle) => (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 700, fontSize: 15, color: PRIMARY }}>
                    <PercentageOutlined /> {cle}%
                </span>
            ),
            sorter: (a, b) => a.cle - b.cle,
        },
        {
            title: "Période de validité", key: "periode", width: 200,
            render: (_, r) => (
                <div style={{ fontSize: 12, lineHeight: 1.5 }}>
                    <div><strong>Début :</strong> {dayjs(r.dateDebut).format("DD/MM/YYYY")}</div>
                    <div><strong>Fin :</strong> {dayjs(r.dateFin).format("DD/MM/YYYY")}</div>
                </div>
            ),
        },
        { title: "Statut", key: "statut", align: "center", width: 100, render: (_, r) => statutTag(r) },
        {
            title: "Actions", key: "actions", align: "center", width: 100,
            render: (_, r) => (
                <Space>
                    <Tooltip title={isPoolUser ? "Modifier" : "Réservé au Pool"}>
                        <Button type="link" icon={<EditOutlined />} onClick={() => ouvrirModalEdition(r)} disabled={!isPoolUser} />
                    </Tooltip>
                    <Tooltip title={isPoolUser ? "Supprimer" : "Réservé au Pool"}>
                        <Button type="link" danger icon={<DeleteOutlined />} onClick={() => supprimerCle(r)} disabled={!isPoolUser} />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    const cardStyle = { borderRadius: 14, border: "1px solid #eef0f4", boxShadow: "0 2px 10px rgba(36,33,102,0.05)" };

    return (
        <ConfigProvider locale={frFR}>
            <div className="layout-wrapper layout-content-navbar">
                <div className="layout-container">
                    <SideBar currentMenu={"AUTRES_PARAMETRES"} />
                    <div className="layout-page">
                        <NavBar />
                        <div className="content-wrapper" style={{ background: "#f6f7fb" }}>
                            <div className="container-xxl flex-grow-1 container-p-y">

                                {/* En-tête */}
                                <div style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, #1890ff 100%)`, borderRadius: 14, padding: "18px 22px", marginBottom: 18, color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                                    <div>
                                        <Title level={4} style={{ margin: 0, color: "#fff" }}>
                                            <PieChartOutlined style={{ marginRight: 8 }} />Clés de répartition
                                        </Title>
                                        <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                                            Répartition du SAP entre compagnies, par branche et exercice, sur des périodes de validité
                                        </Text>
                                    </div>
                                    {isPoolUser && (
                                        <Space wrap>
                                            <Button icon={<ExperimentOutlined />} onClick={() => navigate("/autre-parametres/cle-de-repartition/simulation")}
                                                style={{ borderRadius: 8, fontWeight: 600 }}>Simulation</Button>
                                            <Button icon={<ImportOutlined />} onClick={() => setImportModalVisible(true)}
                                                style={{ borderRadius: 8, fontWeight: 600 }}>Importer</Button>
                                            <Button type="primary" icon={<PlusOutlined />} onClick={ouvrirModalCreation}
                                                style={{ background: "#fff", color: PRIMARY, borderRadius: 8, fontWeight: 700, border: "none" }}>Nouvelle clé</Button>
                                        </Space>
                                    )}
                                </div>

                                {/* Statistiques */}
                                <Row gutter={[16, 16]} style={{ marginBottom: 18 }}>
                                    {[
                                        { t: "Total clés", v: stats.totalCles, icon: <PieChartOutlined />, c: PRIMARY },
                                        { t: "Compagnies", v: stats.compagniesUniques, icon: <BankOutlined />, c: "#0891b2" },
                                        { t: "Taux moyen", v: stats.tauxMoyen, precision: 2, suffix: "%", icon: <TeamOutlined />, c: "#d97706" },
                                        { t: "Clés actives", v: stats.clesActives, icon: <CheckCircleOutlined />, c: "#16a34a" },
                                    ].map((s) => (
                                        <Col xs={12} lg={6} key={s.t}>
                                            <Card style={cardStyle} bodyStyle={{ padding: 16, display: "flex", alignItems: "center", gap: 12 }}>
                                                <span style={{ width: 42, height: 42, borderRadius: 11, background: `${s.c}14`, color: s.c, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>{s.icon}</span>
                                                <Statistic title={s.t} value={s.v} precision={s.precision} suffix={s.suffix}
                                                    valueStyle={{ color: s.c, fontWeight: 700, fontSize: 22 }} />
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>

                                {/* Filtres */}
                                <Card style={{ ...cardStyle, marginBottom: 18 }} bodyStyle={{ padding: 16 }}>
                                    <Row gutter={[12, 12]} align="middle">
                                        <Col xs={24} md={6}>
                                            <Select allowClear showSearch optionFilterProp="label" style={{ width: "100%" }}
                                                placeholder="Branche" value={filtreBranche || undefined}
                                                onChange={(v) => setFiltreBranche(v || null)}
                                                options={branches.map((b) => ({ value: b.codeBranche, label: b.nom || b.codeBranche }))} />
                                        </Col>
                                        <Col xs={24} md={6}>
                                            <Select allowClear showSearch optionFilterProp="label" style={{ width: "100%" }}
                                                placeholder="Compagnie" value={filtreCompagnie || undefined}
                                                onChange={(v) => setFiltreCompagnie(v || null)}
                                                options={compagnies.map((c) => ({ value: c.code, label: c.raisonSociale || c.code }))} />
                                        </Col>
                                        <Col xs={24} md={5}>
                                            <Select allowClear showSearch optionFilterProp="label" style={{ width: "100%" }}
                                                placeholder="Exercice" value={filtreExercice || undefined}
                                                onChange={(v) => setFiltreExercice(v || null)}
                                                options={exercices.map((e) => ({ value: e.exerciceId, label: e.libelleExercice }))} />
                                        </Col>
                                        <Col xs={24} md={5}>
                                            <Input allowClear prefix={<SearchOutlined />} placeholder="Rechercher…"
                                                value={recherche} onChange={(e) => setRecherche(e.target.value)} />
                                        </Col>
                                        <Col xs={24} md={2}>
                                            <Space>
                                                <Tooltip title="Actualiser"><Button icon={<ReloadOutlined />} onClick={chargerCles} /></Tooltip>
                                                <Button type="link" onClick={reinitialiserFiltres} style={{ padding: 0 }}>Réinit.</Button>
                                            </Space>
                                        </Col>
                                    </Row>
                                </Card>

                                {/* Tableau */}
                                <Card style={cardStyle} bodyStyle={{ padding: 8 }}>
                                    <Table
                                        rowKey="cleRepartitionId"
                                        loading={loading}
                                        columns={colonnes}
                                        dataSource={clesFiltrees}
                                        size="small"
                                        scroll={{ x: "max-content" }}
                                        locale={{ emptyText: <Empty description="Aucune clé de répartition" /> }}
                                        pagination={{ defaultPageSize: 15, showSizeChanger: true, pageSizeOptions: ["15", "30", "50", "100"], showTotal: (t) => `${t} clé(s)` }}
                                    />
                                </Card>
                            </div>
                            <Footer />
                            <div className="content-backdrop fade"></div>
                        </div>
                    </div>
                </div>

                {/* Modal création / édition */}
                <Modal
                    title={editingCle ? "Modifier la clé de répartition" : "Nouvelle clé de répartition"}
                    open={modalVisible} onCancel={() => setModalVisible(false)} footer={null} width={700} destroyOnClose
                >
                    <Alert type="info" showIcon icon={<InfoCircleOutlined />} style={{ marginBottom: 16 }}
                        message="Les périodes de validité ne doivent pas se chevaucher pour une même compagnie et branche." />
                    <Form form={form} layout="vertical" onFinish={validerFormulaire}>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="compagnie" label="Compagnie" rules={[{ required: true, message: "Compagnie requise" }]}>
                                    <Select showSearch optionFilterProp="label" placeholder="Sélectionner"
                                        options={compagnies.map((c) => ({ value: c.code, label: c.raisonSociale || c.code }))} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="branche" label="Branche" rules={[{ required: true, message: "Branche requise" }]}>
                                    <Select showSearch optionFilterProp="label" placeholder="Sélectionner"
                                        options={branches.map((b) => ({ value: b.codeBranche, label: b.nom || b.codeBranche }))} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="exercice" label="Exercice" rules={[{ required: true, message: "Exercice requis" }]}>
                                    <Select showSearch optionFilterProp="label" placeholder="Sélectionner"
                                        options={exercices.map((e) => ({ value: e.exerciceId, label: e.libelleExercice }))} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="cle" label="Taux de répartition (%)" rules={[{ required: true, message: "Taux requis" }]}>
                                    <InputNumber min={0} max={100} step={0.01} precision={2} addonAfter="%" style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item name="periode" label="Période de validité" rules={[{ required: true, message: "Période requise" }]}>
                                    <RangePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item name="libelle" label="Libellé / Description">
                                    <Input.TextArea rows={3} placeholder="Commentaire (facultatif)" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                            <Button onClick={() => setModalVisible(false)}>Annuler</Button>
                            <Button type="primary" htmlType="submit" style={{ background: PRIMARY }}>
                                {editingCle ? "Modifier" : "Créer"}
                            </Button>
                        </div>
                    </Form>
                </Modal>

                <ClesRepartitionImport
                    visible={importModalVisible}
                    onClose={() => setImportModalVisible(false)}
                    onImportSuccess={() => { setImportModalVisible(false); chargerCles(); }}
                />

                <div className="layout-overlay layout-menu-toggle"></div>
                <div className="drag-target"></div>
            </div>
        </ConfigProvider>
    );
};

export default ClesRepartitionV2;
